import { computed, inject, provide, ref, type InjectionKey, type Ref } from 'vue';
import { useCreateDirectoryMutation } from '../../data/useCreateDirectoryMutation';
import type { FileManagerMountsState } from './useFileManagerMounts';
import type { FileManagerNavigationState } from './useFileManagerNavigation';

export type FileManagerCreateFolderState = {
  isModalOpen: Ref<boolean>;
  // Exposed alongside the mutation's own pending flag so the toolbar's "New" button (and any
  // future call site) can keep its disabled-while-pending behavior without owning the mutation.
  isPending: Ref<boolean>;
  // Whether a folder can be created in a SPECIFIC path — path-aware because the virtual root has no
  // drive to write to: it lists the site's document libraries, and SharePoint will not let anyone
  // create a library through this API (D9's whole point). Also false while the mounts are still
  // loading, and for a mount the accessibility probe hid.
  canCreateFolderAt: (path: string) => boolean;
  // The currentPath case, for FileExplorerHandler's "New" button disable. Same guard as
  // useFileManagerUpload's canUpload — without it, "New > Directory" would fire a request with no
  // drive to aim at.
  canCreateFolder: Ref<boolean>;
  // Omit parentPath to target currentPath (the toolbar/grid's own "New folder" convention); pass one
  // explicitly for a call site that means something other than "wherever the main pane currently
  // is" — e.g. a folder row's own context-menu entry, which targets that folder.
  openModal: (parentPath?: string) => void;
  createFolder: (name: string) => void;
};

export const fileManagerCreateFolderKey: InjectionKey<FileManagerCreateFolderState> =
  Symbol('fileManagerCreateFolder');

// `navigation` and `mounts` are taken as parameters for the same reason `useFileManagerUpload`
// does — this factory is provided once at the `FileManager.vue` root, and a component can never
// inject a value it provided itself in the same setup() call.
export const provideFileManagerCreateFolder = (
  navigation: FileManagerNavigationState,
  mounts: Pick<FileManagerMountsState, 'resolve' | 'hasBackingStore'>,
) => {
  const { currentPath } = navigation;
  const { mutate, isPending } = useCreateDirectoryMutation(mounts);

  const isModalOpen = ref(false);
  // Captured at open time (same convention as rename/delete's pendingItem/pendingTarget) rather than
  // read live from currentPath.value inside createFolder — so a caller that means "a specific
  // folder", not "wherever currentPath happens to be at submit time", stays correct even if the user
  // navigates the tree while the modal is still open.
  const pendingParentPath = ref('');

  // Whether anything backs the path is the whole question here — deliberately NOT isNavigablePath.
  // The virtual root and a namespace level are navigable but not writable: their rows come from the
  // mount table rather than from a store, and the only thing a "folder" would be there is a document
  // library, which this API cannot create (D9).
  const canCreateFolderAt = (path: string) => mounts.hasBackingStore(path);

  const canCreateFolder = computed(() => canCreateFolderAt(currentPath.value));

  // Both guards check the TARGET path, not currentPath: the modal may have been opened for a folder
  // the main pane is not currently in.
  const openModal = (parentPath?: string) => {
    const target = parentPath ?? currentPath.value;
    if (!canCreateFolderAt(target)) return;
    pendingParentPath.value = target;
    isModalOpen.value = true;
  };

  const createFolder = (name: string) => {
    if (!canCreateFolderAt(pendingParentPath.value)) return;
    mutate({ parentPath: pendingParentPath.value, name });
  };

  const state: FileManagerCreateFolderState = {
    isModalOpen,
    isPending,
    canCreateFolderAt,
    canCreateFolder,
    openModal,
    createFolder,
  };
  provide(fileManagerCreateFolderKey, state);
  return state;
};

export const useFileManagerCreateFolder = () => {
  const state = inject(fileManagerCreateFolderKey);
  if (!state) {
    throw new Error(
      'useFileManagerCreateFolder() must be called within a component tree provided by provideFileManagerCreateFolder()',
    );
  }
  return state;
};
